<script lang="ts" setup>
import SummaryBanner from './SummaryBanner.vue';
import NoAdditionalAddons from './NoAdditionalAddons.vue';
import SingleAddons from './SingleAddons.vue';
import GroupAddons from './GroupAddons.vue';
import SchemeSummary from '../SchemeSummary.vue';
import DocumentListBox from '../DocumentListBox.vue';
import { useRiskStore } from '@/stores/riskData'
import { storeToRefs } from 'pinia'
import { computed, toRaw, ref } from 'vue';
import StickySummaryBar from '../StickySummaryBar.vue';
import { UpdateQuote as updateQuoteAPI } from '@/composables/useQuoteAPIRequests'
import LoaderOverlay from '@/components/Common/LoaderOverlay.vue';
const store = useRiskStore()
const { productOutput, action } = storeToRefs(store)
let loading = ref(false);

defineOptions({ name: 'AddonsTab' });

const selectedScheme = computed(() => {
    return productOutput.value.ReturnSchemes.find((scheme: any) => scheme.IsSelected);
});

const anyAddonToBeDisplayed = computed(() => {
    if (selectedScheme.value.CreatedDate !== undefined && selectedScheme.value.CreatedDate != "0001-01-01T00:00:00") {
        for (let i = 0; i < selectedScheme.value.Addons.length; i++) {
            if (!selectedScheme.value.Addons[i].IsIncluded) {
                return true;
            }
        }
        for (let i = 0; i < selectedScheme.value.GroupedAddons.length; i++) {
            for (let j = 0; j < selectedScheme.value.GroupedAddons[i].GroupAddons.length; j++) {
                if (!selectedScheme.value.GroupedAddons[i].GroupAddons[j].IsIncluded) {
                    return true;
                }
            }
        }
    }
});

function handleSelect(id: string, addonType: "grouped" | "single") {
  store.selectAddon(id, selectedScheme.value.SchemeId, addonType);
}

const emit = defineEmits<{
  (e: 'nav-back'): void
  (e: 'nav-next'): void
  (e: 'show-operator-script', tabId: string): void
}>();

const props = defineProps<{
    action: string
    productId: string
    quoteId: string
}>();

const handleAddonsNext = async () => {
    loading.value = true;

  try {
    const { UpdateQuote } = await updateQuoteAPI(
      action.value,
      props.productId,
      props.quoteId,
      {
        schemeId: selectedScheme.value.SchemeId,
        page: 3,
        showReviewLists: false,
        addonQuoteRequest: true,
        transactionType: action.value,
        cover: toRaw(selectedScheme.value),
        selectOnly: true,
        singleInsurer: true,
      }
    )

    if (UpdateQuote?.ReturnSchemes) {
      store.setPayload({ ...(store.payload ?? {}), productOutput: UpdateQuote })
    }

    loading.value = false;
    
  } catch (e) {
    console.error('Error saving addons:', e)
    loading.value = false;
  }
  finally {
    emit('nav-next')
    emit('show-operator-script', 'confirmation')
  }
}

function handleNavBack() { emit('nav-back') }
</script>
<template>
    <div class="mT-15">
        <!-- Top row: scheme summary + document list -->
        <StickySummaryBar :selectedScheme="selectedScheme" :action="action">
        <template #summary-bar-content>
            <!-- Top row: scheme summary + document list -->
            <div class="row mb-3 justify-content-between">
                <div class="col-12 col-lg-5 d-flex mb-2 mb-lg-0" v-if="productOutput?.Documents?.length > 0">
                    <DocumentListBox :documents="productOutput.Documents" />
                </div>
                <div class="col-12 col-lg-6 mb-3 mb-lg-0 d-flex">
                    <SchemeSummary v-if="selectedScheme" :scheme="selectedScheme" :action="action" :hideProceed="true" />
                </div>
            </div>
        </template>
    </StickySummaryBar>

        <!-- Addons full-width -->
        <div class="row">
            <div class="col-12">
                <SummaryBanner v-if="action?.toLowerCase().includes('mta')" />

                <template v-if="selectedScheme">
                    <NoAdditionalAddons v-if="!anyAddonToBeDisplayed" />

                    <template
                        v-for="(addon, index) in selectedScheme.Addons"
                        :key="addon.Code"
                    >
                        <SingleAddons
                            @select="handleSelect"
                            v-if="!addon.IsIncluded"
                            :addon="addon"
                            :action="action"
                            :index="Number(index)"
                            :productOutput="productOutput"
                        />
                    </template>

                    <template
                        v-for="(groupAddon, index) in selectedScheme.GroupedAddons"
                        :key="groupAddon.Code"
                    >
                        <GroupAddons
                            @select="handleSelect"
                            v-if="!groupAddon.IsIncluded"
                            :groupAddon="groupAddon"
                            :index="Number(index)"
                            :productOutput="productOutput"
                            :action="action"
                        />
                    </template>
                </template>
            </div>
        </div>

        <hr />
        <div class="d-flex justify-content-between">
            <button class="btn btn-outline-primary" id="quoteBackBtn"  @click="handleNavBack"><em class="far fa-chevron-left mR-5"></em> Back</button>
            <span>
                <button v-if="handleAddonsNext" class="btn btn-outline-primary" id="quoteNextBtn" @click="handleAddonsNext">Next <em class="far fa-chevron-right mL-5"></em></button>
            </span>
        </div>
    </div>

    <LoaderOverlay
        :show="loading"
        :title="'Loading, please wait'"
        :text="'We are processing your request'"
    />

    <style>
        .addon-description h2 {
            font-size: 21px;
        }
    </style>
</template>
